/**
 * `idb-store` (W45-F5-S1, #1476) — a tiny promise-based IndexedDB key/value
 * store, blob-safe, with a swappable backend.
 *
 * Why hand-rolled instead of `idb-keyval`?
 *   The offline evidence queue stores `Blob`s (photos / documents), which
 *   IndexedDB persists natively without base64 round-tripping. We need a
 *   store that (a) survives app restarts, (b) is trivially mockable in unit
 *   tests, and (c) adds zero hard dependency. This module is the (a)+(c)
 *   half; the `IdbLike` interface is the (b) seam — tests inject an
 *   in-memory implementation, the app uses the real IndexedDB-backed one.
 *
 * The store is intentionally minimal: one object store, string keys, any
 * structured-cloneable value (Blobs included). No indexes, no migrations —
 * the queue layer (`evidence-queue.ts`) owns all higher-level semantics.
 */

/**
 * The narrow contract the evidence queue depends on. Implemented by the real
 * IndexedDB store below and by the in-memory fake used in tests.
 */
export interface IdbLike {
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

const DB_NAME = 'senviro-offline';
const STORE_NAME = 'kv';
const DB_VERSION = 1;

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB.open failed'));
  });
}

function runTx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const request = op(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB tx failed'));
  });
}

/**
 * The real IndexedDB-backed implementation. Opens the database lazily and
 * caches the handle. Throws on construction-time use if IndexedDB is absent
 * (callers should branch on {@link createIdbStore} returning a fallback).
 */
class IndexedDbStore implements IdbLike {
  private dbPromise: Promise<IDBDatabase> | undefined;

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) this.dbPromise = openDb();
    return this.dbPromise;
  }

  async get<T>(key: string): Promise<T | undefined> {
    const db = await this.db();
    const value = await runTx<T | undefined>(db, 'readonly', (store) =>
      store.get(key),
    );
    return value;
  }

  async set<T>(key: string, value: T): Promise<void> {
    const db = await this.db();
    await runTx(db, 'readwrite', (store) => store.put(value, key));
  }

  async delete(key: string): Promise<void> {
    const db = await this.db();
    await runTx(db, 'readwrite', (store) => store.delete(key));
  }

  async keys(): Promise<string[]> {
    const db = await this.db();
    const result = await runTx<IDBValidKey[]>(db, 'readonly', (store) =>
      store.getAllKeys(),
    );
    return result.map((k) => String(k));
  }
}

/**
 * In-memory fallback used when IndexedDB is unavailable (SSR, private-mode
 * lockdowns, very old browsers). Not persistent — the queue degrades to a
 * session-only buffer rather than crashing. Also handy as a test double.
 */
export class InMemoryStore implements IdbLike {
  private readonly map = new Map<string, unknown>();

  get<T>(key: string): Promise<T | undefined> {
    return Promise.resolve(this.map.get(key) as T | undefined);
  }

  set<T>(key: string, value: T): Promise<void> {
    this.map.set(key, value);
    return Promise.resolve();
  }

  delete(key: string): Promise<void> {
    this.map.delete(key);
    return Promise.resolve();
  }

  keys(): Promise<string[]> {
    return Promise.resolve([...this.map.keys()]);
  }
}

/**
 * Returns the best available store: IndexedDB when present, else an in-memory
 * fallback. The fallback keeps the capture flow working (no crash) even when
 * persistence isn't available — the user just loses the queue on reload.
 */
export function createIdbStore(): IdbLike {
  return isIndexedDbAvailable() ? new IndexedDbStore() : new InMemoryStore();
}
